import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, TextButton } from '@xing-com/button';
import type { UploadApplication } from '@xing-com/crate-common-graphql-types';

import { DeleteVideoButton } from './components/delete-video-button/delete-video-button';
import { UploadContainer } from './components/upload/upload-container';
import { VideoForm } from './components/video-form/video-form';
import { useUploadRequest } from './hooks/use-upload-request';
import type {
  DisplayValues,
  UpdateParams,
  VideoData,
} from './types/video-form';
import * as Styled from './video-upload.styles';

export type VideoFormCreateProps = {
  application: UploadApplication;
  onError?: (error: string | Error) => void;
  onDelete?: () => void;
  onSave?: ({ mediaId, videoId }: { mediaId: string; videoId: string }) => void;
  onUploadVideoComplete?: ({
    mediaId,
    videoId,
  }: {
    mediaId: string;
    videoId: string;
  }) => void;
  onCancel?: () => void;
  onValueChange?: () => void;
  createVideoMutation: (
    fileName: string
  ) => Promise<{ mediaId: string; uploadUrl: string; videoId: string }>;
  deleteVideoMutation: (mediaId: string) => Promise<void>;
  updateVideoMutation: (mediaId: string, videoData: VideoData) => Promise<void>;
};
export const VideoFormCreate: React.FC<VideoFormCreateProps> = ({
  application,
  onError,
  onDelete,
  onSave,
  onCancel,
  onValueChange,
  onUploadVideoComplete,
  deleteVideoMutation,
  updateVideoMutation,
  createVideoMutation,
}) => {
  const { $t } = useIntl();
  const uploadRequest = useUploadRequest();

  const [displayValues, setDisplayValues] = useState<DisplayValues>({});
  const [updates, setUpdate] = useState<UpdateParams>({});

  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  const [mediaId, setMediaId] = useState<string | undefined>();
  const [videoId, setVideoId] = useState<string | undefined>();
  const [titleError, setTitleError] = useState(false);

  const updateValue = (
    type: string,
    value: File | string | undefined
  ): void => {
    if (typeof value !== 'undefined') {
      setUpdate({ ...updates, [type]: value });
      setDisplayValues({
        ...displayValues,
        [type]: typeof value === 'string' ? value : value?.name,
      });
      onValueChange?.();
    }
  };
  const handleDeleteVideo = async (): Promise<void> => {
    if (!mediaId) return;

    setIsDeleting(true);

    try {
      await deleteVideoMutation(mediaId);

      setIsDeleting(false);
      onDelete?.();
    } catch {
      setIsDeleting(false);
      onError?.($t({ id: 'VIDEO_DELETE_ERROR' }));
    }
  };
  const handleOnSave = async (): Promise<void> => {
    if (!mediaId || !videoId) return;

    setIsUpdating(true);

    const videoData: VideoData = {};

    if (Object.keys(updates).length === 0) {
      onSave?.({ mediaId, videoId });
      return;
    }

    try {
      if (typeof updates?.title !== 'undefined') {
        videoData.title = updates?.title.trim();
      }

      // English subtitle
      if (
        typeof updates?.englishSubtitle !== 'undefined' &&
        updates?.englishSubtitle !== null
      ) {
        const englishSubtitleUploadId = await uploadRequest({
          file: updates?.englishSubtitle,
          application,
        });
        videoData.englishSubtitle = {
          filename: updates?.englishSubtitle?.name || null,
          uploadId: englishSubtitleUploadId,
        };
      }

      // German subtitle
      if (
        typeof updates?.germanSubtitle !== 'undefined' &&
        updates?.germanSubtitle !== null
      ) {
        const germanSubtitleUploadId = await uploadRequest({
          file: updates?.germanSubtitle,
          application,
        });
        videoData.germanSubtitle = {
          filename: updates?.germanSubtitle?.name || null,
          uploadId: germanSubtitleUploadId,
        };
      }

      // Preview Image
      if (
        typeof updates?.previewImage !== 'undefined' &&
        updates?.previewImage
      ) {
        const thumbnailUploadId = await uploadRequest({
          file: updates?.previewImage,
          application,
        });
        videoData.thumbnailUploadId = thumbnailUploadId;
      }

      await updateVideoMutation(mediaId, videoData);

      onSave?.({ mediaId, videoId });
    } catch (error) {
      onError?.(error.message);
    } finally {
      setIsUpdating(false);
    }
  };
  const handleOnCancel = (): void => {
    handleDeleteVideo();
    onCancel?.();
  };

  const isSaveButtonDisabled = isDeleting || titleError;

  return (
    <>
      <UploadContainer
        previewImage={updates.previewImage}
        createVideoMutation={createVideoMutation}
        onError={onError}
        onUploadVideoComplete={({ mediaId, videoId }) => {
          setMediaId(mediaId);
          setVideoId(videoId);
          setIsUploaded(true);
          onUploadVideoComplete?.({ mediaId, videoId });
        }}
      />

      {isUploaded ? (
        <>
          <VideoForm
            displayValues={displayValues}
            setTitleError={setTitleError}
            updateValue={updateValue}
            onError={(error) => onError?.(error)}
          />

          <Styled.CtaContainer>
            <DeleteVideoButton
              loading={isDeleting}
              onConfirmDialog={handleDeleteVideo}
            />
            <TextButton
              size="medium"
              disabled={isUpdating || isDeleting}
              onClick={handleOnCancel}
            >
              <FormattedMessage id="VIDEO_UPLOAD_CANCEL" />
            </TextButton>
            <Button
              variant="primary"
              size="medium"
              onClick={handleOnSave}
              loading={isUpdating}
              disabled={isSaveButtonDisabled}
            >
              <FormattedMessage id="VIDEO_UPLOAD_SAVE" />
            </Button>
          </Styled.CtaContainer>
        </>
      ) : null}
    </>
  );
};
